/* eslint-disable react/prop-types */
// authContext.js
import { createContext, useState, useContext, useEffect, useCallback, useRef } from "react";
import React from 'react'
import axios from "axios";

const AuthContext = createContext();

// Se renueva antes de que venza para que una request no salga con el token justo
// expirado. Con ExpiresIn de 1 dia, esto dispara alrededor de las 19 horas.
const REFRESH_AT = 0.8;

// setTimeout guarda el delay en un int de 32 bits: mas que esto desborda y se
// dispara de inmediato en loop.
const MAX_TIMEOUT = 2147483647;

export const AuthProvider = ({ children }) => {
  const [tokens, setTokens] = useState(() => {
    // On initial load, try to get the state from local storage
    try {
      const item = window.localStorage.getItem('tokens');
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error("Failed to parse auth token from local storage", error);
      return null;
    }
  });

  // Arranca en true: hasta no saber si la sesión guardada se puede recuperar,
  // nadie debería decidir que hay que volver al login.
  const [bootstrapping, setBootstrapping] = useState(true);

  // Renovación en curso, compartida entre quienes la pidan a la vez (el
  // arranque y el temporizador). Guardar la promesa y no un booleano es lo que
  // permite que el segundo en llegar espere el mismo resultado en vez de
  // rendirse: si abandona, el arranque da la sesión por perdida sin haberla
  // intentado de verdad. En desarrollo pasa siempre, porque StrictMode monta
  // cada efecto dos veces.
  const refreshPromise = useRef(null);

  // Espeja refreshPromise en el estado: un ref no provoca re-render, así que la
  // guarda de rutas no se enteraría de que hay una renovación en curso.
  const [refreshing, setRefreshing] = useState(false);

  // Make sure to keep local storage in sync with the state
  useEffect(() => {
    if (tokens) {
      window.localStorage.setItem('tokens', JSON.stringify(tokens));
    } else {
      window.localStorage.removeItem('tokens');
    }
  }, [tokens]);

  const logout = useCallback(() => {
    // Pasa por el estado a proposito: borrar solo el localStorage dejaba el
    // context con la sesion vieja en memoria hasta recargar la pagina.
    setTokens(null);
  }, []);

  /**
   * Renueva el access token contra Cognito sin pedir el codigo de nuevo.
   *
   * REFRESH_TOKEN_AUTH no dispara MFA, y el refresh token dura 30 dias: esto es
   * lo que evita que el agente tenga que pedir un correo cada dia.
   */
  const refreshTokens = useCallback(async () => {
    if (refreshPromise.current) return refreshPromise.current;
    if (!tokens?.RefreshToken) return false;

    setRefreshing(true);
    refreshPromise.current = (async () => {
      try {
        const response = await axios.post(
          process.env.REACT_APP_BACKEND_HOST + '/auth/refresh',
          { refreshToken: tokens.RefreshToken }
        );
        // Cognito no reemite el RefreshToken en este flujo, asi que se conserva
        // el original; si no, la sesion moriria al primer refresh.
        setTokens((prev) => ({
          ...prev,
          ...response.data,
          RefreshToken: response.data.RefreshToken || prev.RefreshToken,
        }));
        return true;
      } catch (error) {
        // El refresh token vencio o fue revocado: toca volver a login.
        logout();
        return false;
      }
    })();

    try {
      return await refreshPromise.current;
    } finally {
      refreshPromise.current = null;
      setRefreshing(false);
    }
  }, [tokens, logout]);

  /**
   * Recupera la sesión al abrir la aplicación.
   *
   * Con el access token de un día y el refresh de treinta, volver un lunes
   * significaba encontrarse el token vencido. Antes las páginas redirigían al
   * login de inmediato, sin dar tiempo a renovar, y el agente tenía que pedir
   * otro código pese a tener una sesión perfectamente recuperable.
   */
  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      if (tokens?.RefreshToken && !checkAuth(tokens)) {
        await refreshTokens();
      }
      if (!cancelled) setBootstrapping(false);
    };

    boot();
    return () => { cancelled = true; };
    // Solo al montar: es la recuperación inicial, no un efecto continuo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Programa la renovacion y tambien cubre el caso de volver a una pestana que
  // estuvo dormida, donde el timer no llego a dispararse.
  useEffect(() => {
    if (!tokens?.RefreshToken || !tokens?.ExpiresIn || !tokens?.emissionTime) {
      return undefined;
    }

    const expiresAt = tokens.emissionTime + tokens.ExpiresIn * 1000;
    const refreshAt = tokens.emissionTime + tokens.ExpiresIn * 1000 * REFRESH_AT;

    if (Date.now() >= refreshAt) {
      refreshTokens();
      return undefined;
    }

    const delay = Math.min(refreshAt - Date.now(), MAX_TIMEOUT, expiresAt - Date.now());
    const timer = setTimeout(() => { refreshTokens(); }, delay);
    return () => clearTimeout(timer);
  }, [tokens, refreshTokens]);

  return (
    <AuthContext.Provider
      value={{
        tokens,
        setTokens,
        logout,
        refreshTokens,
        bootstrapping,
        refreshing,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const checkAuth = (tokens) => {
  // Se trabaja con milisegundos, por lo que se tiene que multiplicar el ExpiresIn por 1000
  const end = tokens.emissionTime + tokens.ExpiresIn*1000;

  // Sin emissionTime la resta da NaN y la comparacion siempre es false, o sea
  // sesion eterna. Se trata como invalida para forzar el login.
  if (!Number.isFinite(end)) {
    return false;
  }

  if (Date.now() >= end) {
    return false;
  }
  return true;
}

/**
 * Grupos que trae el access token de Cognito.
 *
 * Se lee sin verificar la firma a propósito: sirve solo para decidir qué
 * mostrar en pantalla. Quien manda es el backend, que sí valida el token en
 * cada endpoint de administración.
 */
export const isAdmin = (tokens) => {
    try {
        const payload = JSON.parse(atob(tokens.AccessToken.split('.')[1]));
        return (payload['cognito:groups'] || []).includes('cs-admin');
    } catch (error) {
        return false;
    }
};

export const useAuth = () => useContext(AuthContext);
export default AuthProvider;
